// src/components/agents/FormAddEditAgent.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Box, Paper, Typography, Grid, Button, Divider, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import i18next from "i18next";

import useCreateAgent from "../../hooks/agents/useCreateAgent";
import useUpdateAgent from "../../hooks/agents/useUpdateAgent";
import useGetAgent from "../../hooks/agents/useGetAgent";
import { useUploadMedia } from "../../hooks/media/useUploadMedia";
import useSingleImageField from "../../hooks/media/useSingleImageField";

import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { countries as countryCodes } from "../../constants/countres";

import useGetCountriesForSelect from "../../hooks/countries/useGetCountriesForSelect";
import useGetTermOfPaymentsForSelect from "../../hooks/termOfPayments/useGetTermOfPaymentsForSelect";
import { useGetUsers } from "../../hooks/Users/useGetUsers";
import { transformOptionsToIdName } from "../../utils/forms/transformHelper";

const FormAddEditAgent = ({ isEdit = false, onSubmitSuccess }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    // Hooks
    const { createAgent, isPending: isPendingCreate } = useCreateAgent();
    const { updateAgent: updateAgentAction, isPending: isPendingUpdate } = useUpdateAgent();
    const { agent, isLoading: isLoadingAgent } = useGetAgent(isEdit ? id : null);
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    const { options: countriesOptions } = useGetCountriesForSelect();
    const { forSelect: termOfPaymentsOptions } = useGetTermOfPaymentsForSelect();
    const { Users: users } = useGetUsers(1, 100);

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [countryCode, setCountryCode] = useState(countryCodes[0].phone);
    const [licenseNumber, setLicenseNumber] = useState("");

    const [countryId, setCountryId] = useState("");
    const [userId, setUserId] = useState("");
    const [termOfPaymentId, setTermOfPaymentId] = useState("");
    const [creditDays, setCreditDays] = useState("");

    const [contactPersonName, setContactPersonName] = useState("");
    const [contactPersonPhone, setContactPersonPhone] = useState("");
    const [contactPersonCountryCode, setContactPersonCountryCode] = useState(countryCodes[0].phone);
    const [contactPersonEmail, setContactPersonEmail] = useState("");
    const [contactPersonPosition, setContactPersonPosition] = useState("");

    const [addressAr, setAddressAr] = useState("");
    const [addressEn, setAddressEn] = useState("");
    const [notesAr, setNotesAr] = useState("");
    const [notesEn, setNotesEn] = useState("");

    const [status, setStatus] = useState("active");
    const [errors, setErrors] = useState({});

    // Image Hook
    const img = useSingleImageField({
        initialImage: null,
        uploadMedia,
        isArabic,
    });

    useEffect(() => {
        if (isEdit && agent) {
            setNameAr(agent?.name?.ar || "");
            setNameEn(agent?.name?.en || "");
            setEmail(agent?.email || "");

            // Handle main phone parsing
            const fullPhone = agent?.phone || "";
            if (fullPhone.includes("-")) {
                const parts = fullPhone.split("-");
                setCountryCode(parts[0]);
                setPhone(parts[1]);
            } else {
                setPhone(fullPhone);
            }

            setLicenseNumber(agent?.license_number || "");
            setCountryId(agent?.country_id || "");
            setUserId(agent?.user_id || "");
            setTermOfPaymentId(agent?.term_of_payment_id || "");
            setCreditDays(agent?.credit_days || "");
            setContactPersonName(agent?.contact_person_name || "");

            // Handle contact person phone parsing
            const cpPhone = agent?.contact_person_phone || "";
            if (cpPhone.includes("-")) {
                const cpParts = cpPhone.split("-");
                setContactPersonCountryCode(cpParts[0]);
                setContactPersonPhone(cpParts[1]);
            } else {
                setContactPersonPhone(cpPhone);
            }

            setContactPersonEmail(agent?.contact_person_email || "");
            setContactPersonPosition(agent?.contact_person_position || "");
            setAddressAr(agent?.address?.ar || "");
            setAddressEn(agent?.address?.en || "");
            setNotesAr(agent?.notes?.ar || "");
            setNotesEn(agent?.notes?.en || "");
            setStatus(agent?.status || "active");

            // Sync Image
            if (agent?.media?.length > 0) {
                img.setFromServerImage(agent.media[0]);
            } else if (agent?.image) {
                img.setFromServerImage({ url: agent.image, name: agent.image });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, agent]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            email,
            phone: `${countryCode}-${phone}`,
            license_number: licenseNumber,
            country_id: countryId,
            user_id: userId || null,
            term_of_payment_id: termOfPaymentId,
            credit_days: isCurrentTermCredit ? creditDays : 0,
            contact_person_name: contactPersonName,
            contact_person_phone: contactPersonPhone ? `${contactPersonCountryCode}-${contactPersonPhone}` : "",
            contact_person_email: contactPersonEmail,
            contact_person_position: contactPersonPosition,
            address: {
                ar: addressAr || addressEn,
                en: addressEn || addressAr,
            },
            notes: {
                ar: notesAr || notesEn,
                en: notesEn || notesAr,
            },
            status,
            ...img.getImagePayload(),
        };

        if (isEdit) {
            updateAgentAction(
                { id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                        navigate("/agents");
                    },
                    onError: (err) => setErrors(err?.response?.data?.errors || {}),
                }
            );
        } else {
            createAgent(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                    navigate("/agents");
                },
                onError: (err) => setErrors(err?.response?.data?.errors || {}),
            });
        }
    };

    // Options mapping
    const cOptions = useMemo(() => transformOptionsToIdName(countriesOptions), [countriesOptions]);
    const tOptions = useMemo(() => transformOptionsToIdName(termOfPaymentsOptions), [termOfPaymentsOptions]);
    const uOptions = useMemo(() => {
        return users?.map((u) => ({ id: u.id, name: u.name })) || [];
    }, [users]);

    const isCurrentTermCredit = useMemo(() => {
        const selectedTerm = termOfPaymentsOptions?.find((t) => String(t.value) === String(termOfPaymentId));
        const label = selectedTerm?.label?.toLowerCase() || "";
        return label.includes("credit") || label.includes("آجل");
    }, [termOfPaymentId, termOfPaymentsOptions]);

    const creditDaysOptions = useMemo(() => {
        return Array.from({ length: 100 }, (_, i) => ({
            id: i + 1,
            name: String(i + 1)
        }));
    }, []);

    if (isEdit && isLoadingAgent) return <Typography color="primary" sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {isEdit ? (isArabic ? "تعديل وكيل" : "Edit Agent") : (isArabic ? "إضافة وكيل جديد" : "Add New Agent")}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Logo Upload */}
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexDirection: 'column-reverse', alignItems: 'center' }}>
                            <FormInput
                                type="file"
                                label={isArabic ? "شعار الوكيل" : "Agent Logo"}
                                name="logo"
                                value={img.imagePreview}
                                onChange={img.handleImageChange}
                                isUploading={isUploading}
                                errorMessage={img.imageError || errors.image?.[0]}
                                successMessage={img.imageName && !img.imageError ? (isArabic ? "تم رفع الشعار" : "Logo uploaded") : ""}
                                width={120}
                                height={120}
                                borderRadius="50%"
                                required={false}
                                onDeleted={img.handleDeleted}
                                mediaId={img.imageId}
                            />
                        </Grid>

                        {/* Basic Info */}
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم الوكيل بالعربي"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    error={errors["name.ar"] || errors.name_ar}
                                    required
                                />
                            </Grid>
                        )}
                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter agent name in English"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    error={errors["name.en"] || errors.name_en}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "البريد الإلكتروني" : "Email"}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={errors.email}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="tel"
                                label={isArabic ? "رقم الهاتف" : "Phone Number"}
                                phoneNumber={phone}
                                setPhoneNumber={setPhone}
                                selectedCountryCode={countryCode}
                                setSelectedCountryCode={setCountryCode}
                                error={errors.phone}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الدولة" : "Country"}
                                value={countryId}
                                options={cOptions}
                                onChange={(e) => setCountryId(e.target.value)}
                                error={errors.country_id}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "رقم الترخيص" : "License Number"}
                                value={licenseNumber}
                                onChange={(e) => setLicenseNumber(e.target.value)}
                                error={errors.license_number}
                                required
                            />
                        </Grid>

                        {/* Contact Person Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                {isArabic ? "تفاصيل مسؤول التواصل" : "Contact Person Details"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الاسم" : "Name"}
                                value={contactPersonName}
                                onChange={(e) => setContactPersonName(e.target.value)}
                                error={errors.contact_person_name}
                                required={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "المسمى الوظيفي" : "Position"}
                                value={contactPersonPosition}
                                onChange={(e) => setContactPersonPosition(e.target.value)}
                                error={errors.contact_person_position}
                                required={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "البريد الإلكتروني لمسؤول التواصل" : "Contact Person Email"}
                                type="email"
                                value={contactPersonEmail}
                                onChange={(e) => setContactPersonEmail(e.target.value)}
                                error={errors.contact_person_email}
                                required={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="tel"
                                label={isArabic ? "هاتف مسؤول التواصل" : "Contact Person Phone"}
                                phoneNumber={contactPersonPhone}
                                setPhoneNumber={setContactPersonPhone}
                                selectedCountryCode={contactPersonCountryCode}
                                setSelectedCountryCode={setContactPersonCountryCode}
                                error={errors.contact_person_phone}
                                required={false}
                            />
                        </Grid>

                        {/* Payment & Sales */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                {isArabic ? "المبيعات والدفع" : "Sales & Payment"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <FormInput
                                type="select"
                                label={isArabic ? "مسؤول المبيعات" : "Sales Person"}
                                value={userId}
                                options={uOptions}
                                onChange={(e) => setUserId(e.target.value)}
                                error={errors.user_id}
                                required={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormInput
                                type="select"
                                label={isArabic ? "طريقة الدفع" : "Term of Payment"}
                                value={termOfPaymentId}
                                options={tOptions}
                                onChange={(e) => setTermOfPaymentId(e.target.value)}
                                error={errors.term_of_payment_id}
                                required={false}
                            />
                        </Grid>
                        {isCurrentTermCredit && (
                            <Grid item xs={12} md={4}>
                                <FormInput
                                    label={isArabic ? "أيام الائتمان" : "Credit Days"}
                                    type="select"
                                    value={creditDays}
                                    options={creditDaysOptions}
                                    onChange={(e) => setCreditDays(e.target.value)}
                                    error={errors.credit_days}
                                    required={false}
                                />
                            </Grid>
                        )}

                        {/* Address & Notes */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {showArabic && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        type="textarea"
                                        label="العنوان (بالعربي)"
                                        value={addressAr}
                                        onChange={(e) => setAddressAr(e.target.value)}
                                        error={errors["address.ar"]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        type="textarea"
                                        label="ملاحظات (بالعربي)"
                                        value={notesAr}
                                        onChange={(e) => setNotesAr(e.target.value)}
                                        error={errors["notes.ar"]}
                                    />
                                </Grid>
                            </>
                        )}

                        {showEnglish && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        type="textarea"
                                        label="Address (English)"
                                        value={addressEn}
                                        onChange={(e) => setAddressEn(e.target.value)}
                                        error={errors["address.en"]}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        type="textarea"
                                        label="Notes (English)"
                                        value={notesEn}
                                        onChange={(e) => setNotesEn(e.target.value)}
                                        error={errors["notes.en"]}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الحالة" : "Status"}
                                value={status}
                                options={[
                                    { id: "active", name: isArabic ? "نشط" : "Active" },
                                    { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
                                ]}
                                onChange={(e) => setStatus(e.target.value)}
                                error={errors.status}
                                required={false}
                            />
                        </Grid>
                    </Grid>

                    {/* Actions */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/agents")}
                            sx={{ minWidth: 120, borderRadius: 2 }}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>

                            <Button
                            type="submit"
                            variant="contained"
                            disabled={isPendingCreate || isPendingUpdate || isUploading}
                            sx={{ minWidth: 150, borderRadius: 2 }}
                        >
                            {(isPendingCreate || isPendingUpdate) ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEdit ? (isArabic ? "تعديل" : "Update") : (isArabic ? "إضافة" : "Add")
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default FormAddEditAgent;
